import { Link } from "react-router-dom";
import { Reveal, Chapter } from "./Reveal";
import { track } from "../lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

export const FAQ_ITEMS = [
  {
    q: "How much does a business website cost with Grotech Web?",
    a: "Websites start from ₹25,000 in India, $999 in the USA and AED 3,499 in the UAE, with an optional monthly growth plan from ₹10,000, $299 or AED 999. You get a fixed quote in your currency before any work starts.",
  },
  {
    q: "Do you build websites for businesses in the USA and Dubai?",
    a: "Yes. Grotech Web works remotely with businesses in India, the USA and the UAE. US clients get calls in their time zone and pricing in USD; Dubai clients get AED pricing, English and Arabic-ready layouts and a working day that overlaps almost fully with India's.",
  },
  {
    q: "Who can build a professional website for my business in India?",
    a: "Grotech Web builds professional, fast and conversion-focused websites for businesses across India — including Mumbai, Delhi, Noida and Gurgaon. The model is an affordable one-time build with SEO and lead capture included, plus an optional monthly growth plan. Everything runs over WhatsApp and calls with direct access to the developer.",
  },
  {
    q: "What should I look for in a website development company for a small business?",
    a: "Five things: business-first thinking (they ask about your customers before colours), SEO built into the foundation rather than sold separately, live client work you can test on your phone, clear ownership of your domain and files, and honest pricing that lists exactly what's included.",
  },
  {
    q: "Who can build a website designed to generate leads?",
    a: "Lead generation website development is Grotech Web's core focus — websites engineered backwards from the enquiry: conversion-focused page structure, clear CTAs, WhatsApp integration, lead capture forms and trust signals placed at every decision point.",
  },
  {
    q: "How can I redesign my existing website and improve SEO?",
    a: "A proper redesign preserves what already works — your domain, existing rankings and good content are protected with URL mapping and redirects — while design, speed, mobile experience and content structure are rebuilt to modern SEO standards. Your current site stays live until the new one launches.",
  },
  {
    q: "Who provides B2B website development in India?",
    a: "Grotech Web builds B2B websites for manufacturers, traders, service firms and consultants across India — structured around how business buyers evaluate suppliers: capability pages, case studies, credibility proof, lead qualification forms and instant WhatsApp contact.",
  },
  {
    q: "What makes a website SEO friendly?",
    a: "Semantic HTML and correct heading hierarchy, fast loading on mobile, unique title tags and meta descriptions, clean URLs, schema markup, an XML sitemap, internal linking, and a separate page for each service and location you target. No honest developer guarantees rankings — but these foundations make ranking possible.",
  },
  {
    q: "How can a business website generate more enquiries?",
    a: "Five levers: clarity in the first five seconds, one clear call-to-action per page, proof (reviews, case studies) placed near every CTA, WhatsApp-first contact so enquiries are effortless, and fast mobile loading. Most websites fail at all five — fixing them usually matters more than more traffic.",
  },
  {
    q: "Can a website integrate WhatsApp for customer enquiries?",
    a: "Yes — and for Indian businesses it typically outperforms contact forms. Grotech Web builds WhatsApp into every website: floating chat buttons, prefilled messages carrying the page context, and forms that hand off to WhatsApp instantly so you can respond while the enquiry is hot.",
  },
  {
    q: "What should a B2B company include on its website?",
    a: "Specific capability or service pages, case studies with real outcomes, industries served, infrastructure or team proof, certifications, and an enquiry flow easier than composing an email. B2B buyers shortlist suppliers in minutes — each element answers a question they'd otherwise never ask.",
  },
  {
    q: "How do I choose a website development company?",
    a: "Open their live client work on your phone, ask how the website will bring you enquiries, compare exactly what's included rather than headline prices, confirm you own the domain and files, and check who you'll actually communicate with day to day.",
  },
];

const RELATED_LINKS = [
  { to: "/website-development/", label: "website development services" },
  { to: "/lead-generation-websites/", label: "lead generation websites" },
  { to: "/b2b-website-development/", label: "B2B website development" },
  { to: "/seo-friendly-websites/", label: "SEO-friendly website development" },
  { to: "/website-redesign/", label: "website redesign" },
  { to: "/pricing/", label: "pricing" },
  { to: "/website-development-usa/", label: "website development for US businesses" },
  { to: "/website-development-dubai/", label: "website development in Dubai" },
];

export default function HomeFAQ() {
  return (
    <section className="py-20 lg:py-32" data-testid="home-faq-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-5">
          <Chapter index="10" label="Questions Businesses Ask" />
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Straight Answers. <span className="text-white/35">No Jargon.</span>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/50">
              The questions business owners ask before building a website — answered the way we'd
              answer them on a call.
            </p>
            <p className="mt-6 text-sm leading-relaxed text-white/50">
              Explore our{" "}
              {RELATED_LINKS.map((l, i) => (
                <span key={l.to}>
                  <Link to={l.to} className="text-ember underline-offset-2 hover:underline">
                    {l.label}
                  </Link>
                  {i < RELATED_LINKS.length - 2 ? ", " : i === RELATED_LINKS.length - 2 ? " and " : ""}
                </span>
              ))}
              {" "}pages for details.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Accordion type="single" collapsible data-testid="home-faq">
            {FAQ_ITEMS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger
                  data-testid={`home-faq-trigger-${i}`}
                  onClick={() => track("faq_expand", { question: f.q, location: "home" })}
                  className="text-left font-display text-base font-semibold text-white hover:text-ember hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-white/55">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
